/**
 * Composable for extracting dominant color from cover art images
 */

import { ref, watch, type Ref } from 'vue'
import type { DynamicTheme } from '../types'

const FALLBACK_COLOR = '#884AFF'
const FALLBACK_RGB: [number, number, number] = [136, 74, 255]
const CANVAS_SIZE = 50

const cache = new Map<string, DynamicTheme>()

export interface UseColorExtractionReturn {
  theme: Ref<DynamicTheme>
  isExtracting: Ref<boolean>
  extract: () => void
  reset: () => void
}

function getLuminance(r: number, g: number, b: number): number {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255
}

function getSaturation(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  if (max === 0) return 0
  return (max - min) / max
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`
}

function getDefaultTheme(): DynamicTheme {
  return {
    dominantColor: FALLBACK_COLOR,
    dominantColorRgb: FALLBACK_RGB,
    isDark: false,
  }
}

/**
 * Extracts the dominant color from an image URL using a small canvas
 */
export function useColorExtraction(imageUrl: Ref<string | undefined>): UseColorExtractionReturn {
  const theme = ref<DynamicTheme>(getDefaultTheme())
  const isExtracting = ref(false)

  function extractFromImage(url: string): void {
    if (cache.has(url)) {
      theme.value = cache.get(url)!
      return
    }

    isExtracting.value = true

    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = CANVAS_SIZE
        canvas.height = CANVAS_SIZE
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          isExtracting.value = false
          return
        }

        ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE)
        const imageData = ctx.getImageData(0, 0, CANVAS_SIZE, CANVAS_SIZE).data

        // Simple dominant color: aggregate pixel colors weighted by saturation
        const colorBuckets = new Map<string, { r: number; g: number; b: number; count: number }>()

        for (let i = 0; i < imageData.length; i += 4) {
          const r = imageData[i]
          const g = imageData[i + 1]
          const b = imageData[i + 2]

          // Skip very dark or very light pixels (background noise)
          const lum = getLuminance(r, g, b)
          if (lum < 0.05 || lum > 0.95) continue

          // Quantize to reduce color space
          const qr = Math.round(r / 32) * 32
          const qg = Math.round(g / 32) * 32
          const qb = Math.round(b / 32) * 32
          const key = `${qr},${qg},${qb}`

          const existing = colorBuckets.get(key)
          if (existing) {
            existing.r += r
            existing.g += g
            existing.b += b
            existing.count++
          } else {
            colorBuckets.set(key, { r, g, b, count: 1 })
          }
        }

        // Score buckets by count * saturation to prefer vibrant colors
        let bestBucket = { r: FALLBACK_RGB[0], g: FALLBACK_RGB[1], b: FALLBACK_RGB[2], count: 0 }
        let bestScore = 0
        const totalPixels = (CANVAS_SIZE * CANVAS_SIZE)

        for (const bucket of colorBuckets.values()) {
          if (bucket.count < 3) continue
          const avgR = bucket.r / bucket.count
          const avgG = bucket.g / bucket.count
          const avgB = bucket.b / bucket.count
          const sat = getSaturation(avgR, avgG, avgB)
          const lum = getLuminance(avgR, avgG, avgB)
          // Prefer saturated colors with reasonable luminance
          const lumBonus = (lum > 0.15 && lum < 0.85) ? 1.5 : 0.5
          const score = (bucket.count / totalPixels) * (sat + 0.1) * lumBonus
          if (score > bestScore) {
            bestScore = score
            bestBucket = bucket
          }
        }

        const avgR = bestBucket.count > 0 ? Math.round(bestBucket.r / bestBucket.count) : FALLBACK_RGB[0]
        const avgG = bestBucket.count > 0 ? Math.round(bestBucket.g / bestBucket.count) : FALLBACK_RGB[1]
        const avgB = bestBucket.count > 0 ? Math.round(bestBucket.b / bestBucket.count) : FALLBACK_RGB[2]

        const result: DynamicTheme = {
          dominantColor: rgbToHex(avgR, avgG, avgB),
          dominantColorRgb: [avgR, avgG, avgB],
          isDark: getLuminance(avgR, avgG, avgB) < 0.5,
        }

        cache.set(url, result)
        theme.value = result
      } catch {
        // Canvas tainted or other error — use fallback
        theme.value = getDefaultTheme()
      } finally {
        isExtracting.value = false
      }
    }

    img.onerror = () => {
      isExtracting.value = false
      theme.value = getDefaultTheme()
    }

    img.src = url
  }

  function extract(): void {
    const url = imageUrl.value
    if (url) {
      extractFromImage(url)
    } else {
      theme.value = getDefaultTheme()
    }
  }

  function reset(): void {
    theme.value = getDefaultTheme()
  }

  watch(imageUrl, (url) => {
    if (url) {
      extractFromImage(url)
    } else {
      reset()
    }
  }, { immediate: true })

  return {
    theme,
    isExtracting,
    extract,
    reset,
  }
}
